import styled, { css, keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slideInRight = keyframes`
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(23, 23, 25, 0.52);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease-out;
`;

export const SidePanelOverlay = styled(ModalOverlay)`
  align-items: stretch;
  justify-content: flex-end;
  padding: 0;
`;

export const ModalCard = styled.div<{ $maxWidth?: string; $maxHeight?: string }>`
  width: 100%;
  max-width: ${({ $maxWidth = '600px' }) => $maxWidth};
  max-height: ${({ $maxHeight = '80vh' }) => $maxHeight};
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: ${slideUp} 0.3s ease-out;
  font-family: var(--font-family-base);
`;

export const SidePanelModal = styled.div`
  width: 480px;
  height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 24px;
  overflow: hidden;
  animation: ${slideInRight} 0.3s ease-out;
  font-family: var(--font-family-base);

  @media (max-width: 768px) {
    width: 100vw;
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 16px;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(112, 115, 124, 0.16);
  flex-shrink: 0;
`;

export const SidePanelHeader = styled(ModalHeader)`
  padding: 0 0 20px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  color: #171719;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.02em;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

export const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  color: #171719;
  font-size: 24px;
  line-height: 1;

  &:hover {
    background-color: rgba(112, 115, 124, 0.05);
  }
`;

export const ModalBody = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const SidePanelBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  margin-right: -4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(112, 115, 124, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(112, 115, 124, 0.5);
  }

  @media (max-width: 768px) {
    padding-right: 0;
    margin-right: 0;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

export const ModalFooter = styled.div`
  padding: 20px 24px;
  border-top: 1px solid rgba(112, 115, 124, 0.16);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
`;

export const SidePanelFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(112, 115, 124, 0.16);
  margin-top: auto;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 12px;
  }
`;

const buttonBase = css`
  height: 48px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: var(--font-family-base);

  @media (max-width: 480px) {
    height: 44px;
    font-size: 14px;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase};
  min-width: 140px;
  padding: 0 24px;
  border: none;
  background: #448181;
  color: #fff;

  &:hover:not(:disabled) {
    background: #3a6f6f;
  }

  &:disabled {
    background: rgba(68, 129, 129, 0.5);
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase};
  min-width: 140px;
  padding: 0 24px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: transparent;
  color: rgba(55, 56, 60, 0.8);

  &:hover:not(:disabled) {
    background: rgba(23, 23, 25, 0.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FormRow = styled.div`
  display: flex;
  gap: 16px;

  & > ${FormGroup} {
    flex: 1;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const FormLabel = styled.label`
  margin: 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0.14px;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

export const ReadonlyField = styled.div<{ $multiline?: boolean }>`
  width: 100%;
  min-height: ${({ $multiline }) => ($multiline ? '100px' : '50px')};
  max-height: ${({ $multiline }) => ($multiline ? '150px' : 'none')};
  padding: 12px;
  display: flex;
  align-items: ${({ $multiline }) => ($multiline ? 'flex-start' : 'center')};
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  background: #f4f4f5;
  box-sizing: border-box;
  color: rgba(55, 56, 60, 0.88);
  font-size: 16px;
  line-height: 26px;
  letter-spacing: 0.09px;
  white-space: ${({ $multiline }) => ($multiline ? 'pre-wrap' : 'nowrap')};
  word-break: break-word;
  overflow-y: ${({ $multiline }) => ($multiline ? 'auto' : 'hidden')};

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

export const SelectField = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  font-family: var(--font-family-base);

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

export const InputField = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: #fff;
  box-sizing: border-box;
  color: #171719;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;

  &:focus {
    border-color: #448181;
  }
`;

export const HelperText = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

export const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
`;

export const ButtonRow = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

export const PresetButton = styled.button`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 6px;
  background: transparent;
  color: #171719;
  font-size: 12px;
  font-family: var(--font-family-base);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(112, 115, 124, 0.05);
  }
`;
